import { useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  ButtonContainer,
  InputWrapper,
  Modal,
  PrimaryButton,
  SecondaryButton,
  SectionHeading,
  SectionPreTitle,
  SectionSubTitle,
  SectionTitle,
  SectionWrapper
} from '../atoms';
import { FormattedDate } from '../../common/utils/formatters';
import moment from 'moment';
import { updateCourseEndDate } from '../../contexts/MambuLoanContext';
import { useMutation } from '@tanstack/react-query';
import { UserContext } from '../../contexts';
import React from 'react';

type TErrors = {
  courseEnds?: string;
} | null;

type TFundReleaseDate = string | undefined;

type TRequestFundModal = {
  modalTitle?: string;
  id?: string;
  loanApplicationSalesAgentUserId?: string;
  loanApplicationSalesAgentEmail?: string;
  creationDate: string;
  isModalOpen: boolean;
  handleModalClose: () => void;
  setFakeFunding: React.Dispatch<
    React.SetStateAction<{
      fundingStatus: 'Requested';
      fundingStatusText: string;
    } | null>
  >;
};

const FormFooter = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 45px;
`;

const SuccessIcon = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.main.secondary};
    font-size: 45px !important;
  `
);

const ErrorIcon = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.main.error};
    font-size: 45px !important;
  `
);

const SuccessHeading = styled((props) => <SectionHeading {...props} />)`
  color: ${({ theme }) => theme.colors.main.secondary};
`;

const CourseEndDateModal = ({
  modalTitle,
  id,
  loanApplicationSalesAgentUserId,
  loanApplicationSalesAgentEmail,
  creationDate,
  isModalOpen,
  handleModalClose,
  setFakeFunding
}: TRequestFundModal) => {
  const [errors, setErrors] = useState<TErrors>(null);
  const [courseEnds, setCourseEnds] = useState<TFundReleaseDate>(undefined);
  const [formLoading, setFormLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const { user } = useContext(UserContext);
  const merchantId = user.merchantId;
  const mambuLoanId = id || '';
  const courseEndDate = courseEnds || '';

  const updateCourseEndDateMutation = updateCourseEndDate({
    mambuLoanId,
    merchantId,
    courseEndDate,
    mutate: useMutation,
    loanApplicationSalesAgentUserId,
    loanApplicationSalesAgentEmail,
    setFormLoading,
    setSuccess,
    setError
  });

  const startDate = new Date();
  startDate.setDate(startDate.getDate() + 1);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 90);
  endDate.setHours(0, 0, 0, 0);

  const months = [
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12'
  ];

  const minDate = `${startDate.getFullYear()}-${
    months[startDate.getMonth()]
  }-${startDate.getDate()}`;

  const checkValidity = () => {
    const date = moment(courseEnds, 'DD-MM-YYYY');
    if (courseEnds && date.isValid()) {
      const inputtedDate = new Date(courseEnds);
      inputtedDate.setHours(0, 0, 0, 0);
      if (inputtedDate.getDay() === 0 || inputtedDate.getDay() === 6) {
        setErrors({
          courseEnds: 'Please select a business day'
        });
        return false;
      } else if (inputtedDate < startDate) {
        setErrors({
          courseEnds: 'Release Date should be minimum 1 business day in advance'
        });
        return false;
      } else {
        setErrors(null);
        return true;
      }
    } else {
      setErrors({
        courseEnds: 'Please choose a valid date'
      });
      return false;
    }
  };

  useEffect(() => {
    if (courseEnds !== undefined) {
      checkValidity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseEnds]);

  const fakeFunding = () => {
    setFakeFunding({
      fundingStatus: 'Requested',
      fundingStatusText: 'Requested'
    });
  };

  const resetModalState = () => {
    setError(false);
    setSuccess(false);
  };

  const closeModal = () => {
    if (success === true) {
      fakeFunding();
    }
    resetModalState();
    handleModalClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCourseEnds(e.currentTarget.value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isValid = checkValidity();

    if (isValid) {
      updateCourseEndDateMutation.mutate();
    }
  };

  return (
    <Modal
      handleClose={closeModal}
      isModalVisible={isModalOpen}
      content={
        <>
          {modalTitle && <SectionPreTitle>{modalTitle}</SectionPreTitle>}
          <SectionTitle>{id}</SectionTitle>
          <SectionSubTitle>
            Created:&nbsp;
            <FormattedDate date={creationDate} />
          </SectionSubTitle>

          <div>
            {!success && !error && (
              <SectionWrapper>
                <SectionHeading>Enter Course End Date</SectionHeading>
                <form onSubmit={handleSubmit}>
                  <InputWrapper
                    value={courseEnds}
                    name="endDate"
                    type="date"
                    label="Course End Date"
                    min={minDate}
                    required
                    changeFunction={handleChange}
                    errorMessage={errors ? errors.courseEnds : undefined}
                  />
                  <FormFooter
                    horizontalSpacing="10px"
                    verticalSpacing="0px"
                    justify="flex-end"
                  >
                    <SecondaryButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        closeModal();
                      }}
                    >
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton type="submit" isLoading={formLoading}>
                      Submit
                    </PrimaryButton>
                  </FormFooter>
                </form>
              </SectionWrapper>
            )}
            {success && (
              <SectionWrapper>
                <SuccessIcon className="material-symbols-rounded">
                  sentiment_very_satisfied
                </SuccessIcon>
                <SuccessHeading>Success</SuccessHeading>
                <p>The course end date has successfully been updated.</p>
                <p>Regards, Zeefi</p>
                <SecondaryButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                >
                  Close
                </SecondaryButton>
              </SectionWrapper>
            )}
            {error && (
              <SectionWrapper>
                <ErrorIcon className="material-symbols-rounded">
                  sentiment_very_dissatisfied
                </ErrorIcon>
                <SuccessHeading>Error</SuccessHeading>
                <p>There was an error updating the course end date.</p>
                <p>Please contact support with the id: {id}</p>
                <p>Regards, Zeefi</p>
                <SecondaryButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                >
                  Close
                </SecondaryButton>
              </SectionWrapper>
            )}
          </div>
        </>
      }
    />
  );
};

export { CourseEndDateModal };
